import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS, BOOK_DEMO_URL } from "../data/mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname === to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#EDE5D3] shadow-[0_1px_20px_rgba(31,27,19,0.05)]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/assets/logo-hex-hd.png" alt="MedHive" className="h-9 w-auto" />
          <span className="font-heading text-2xl font-bold tracking-tight text-[#1F1B13]">MedHive</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNav(l.to)}
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-")}`}
              className={`relative text-sm font-semibold capitalize tracking-wide transition-colors duration-200 hover:text-[#B45309] ${
                isActive(l.to) ? "text-[#B45309]" : "text-[#5D564A]"
              }`}
            >
              {l.label}
              {isActive(l.to) && (
                <motion.span layoutId="nav-underline" className="absolute -bottom-1.5 left-0 h-[2px] w-full rounded-full bg-[#E8930C]" />
              )}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-demo"
            className="group inline-flex items-center gap-2 rounded-full bg-[#F0A322] px-5 py-2.5 text-sm font-semibold text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
          >
            book a walkthrough
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </a>
        </div>

        <button
          className="text-[#1F1B13] lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-[#EDE5D3] bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => handleNav(l.to)}
                  className="rounded-lg px-3 py-3 text-left text-sm font-semibold capitalize text-[#5D564A] transition-colors hover:bg-[#FAF6EE] hover:text-[#B45309]"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#F0A322] px-5 py-3 text-sm font-semibold text-[#231A07]"
              >
                book a walkthrough <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
Navbar;
